// 收件箱 / 回收站表格 —— InboxPage 与 TrashPage 共用。
// 处理：数据加载、空状态、行点击跳详情、星标切换（乐观）、未读→已读切换（乐观）、
//      批量勾选 + 移动到文件夹/删除/恢复/彻底删除、加载更多。
//
// 响应式：sm 及以上是 9 列表格；sm 以下换成卡片列表（MessageCard），
//         两者共用同一份 items 与 useRowActions 里的 handler。
//
// mode='trash' 时行为差异：工具条换成「恢复 / 彻底删除」，彻底删除二次确认，
// 空状态文案不同；其余（星标、已读切换、跳详情）保持一致。

import { Loader2, RotateCcw, Star, StarOff, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useMoveMessages } from '../lib/folders.js';
import {
  useFilteredMessages,
  useMarkRead,
  useMarkUnread,
  usePurgeMessages,
  useRestoreMessages,
  useToggleStar,
  useTrashMessages,
  type InboxTab,
  type MessageDTO,
  type MessagesFilter,
} from '../lib/messages.js';
import { formatRelative } from '../lib/time.js';

import { AuthSuspiciousBadge } from './AuthSuspiciousBadge.js';
import { MoveToFolderSelect } from './folders/MoveToFolderSelect.js';
import { LabelChip } from './labels/LabelChip.js';
import { SenderAvatar } from './SenderAvatar.js';

export type MessageListMode = 'inbox' | 'trash';

interface Props {
  filter: MessagesFilter;
  mode?: MessageListMode;
}

export function MessageList({ filter, mode = 'inbox' }: Props) {
  const q = useFilteredMessages(filter);
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const tabLabel: InboxTab = filter.tab ?? 'all';
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());

  const trash = useTrashMessages();
  const restore = useRestoreMessages();
  const purge = usePurgeMessages();
  const move = useMoveMessages();
  const busy = trash.isPending || restore.isPending || purge.isPending || move.isPending;
  const currentFolder = typeof filter.folder === 'number' ? filter.folder : null;

  // 筛选/tab 变了就清空勾选，避免"看不见的选中项"被后续操作误删。
  const filterKey = JSON.stringify(filter);
  useEffect(() => {
    setSelected(new Set());
  }, [filterKey]);

  const visibleIds = items.map((m) => m.id);
  const selectedIds = visibleIds.filter((id) => selected.has(id));
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(visibleIds));
  };
  const runBulk = (run: { mutate: (ids: number[], opts?: { onSuccess?: () => void }) => void }) => {
    const ids = selectedIds;
    if (ids.length === 0) return;
    run.mutate(ids, { onSuccess: () => setSelected(new Set()) });
  };
  const confirmPurge = () => {
    const n = selectedIds.length;
    if (n === 0) return;
    if (
      !window.confirm(
        `彻底删除这 ${String(n)} 封邮件？附件与原始邮件会一并从存储中删除，无法恢复。`,
      )
    ) {
      return;
    }
    runBulk(purge);
  };
  const moveSelected = (folderId: number | null) => {
    const ids = selectedIds;
    if (ids.length === 0) return;
    move.mutate({ ids, folderId }, { onSuccess: () => setSelected(new Set()) });
  };

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500 dark:text-slate-400">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        <span className="text-sm">加载中...</span>
      </div>
    );
  }

  if (q.isError) {
    return (
      <div className="mx-4 my-8 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
        加载失败：{q.error?.message ?? '未知错误'}
      </div>
    );
  }

  if (items.length === 0) {
    const emptyHint =
      tabLabel === 'unread'
        ? '没有未读邮件'
        : tabLabel === 'read'
          ? '没有已读邮件'
          : tabLabel === 'forwarded'
            ? '没有已转发邮件'
            : mode === 'trash'
              ? '回收站是空的'
              : typeof filter.folder === 'number'
                ? '这个文件夹还是空的 — 在收件箱勾选邮件后「移动到…」，或在规则里加「移动到文件夹」动作'
                : '暂无邮件 — 等 Email Worker 入库邮件后可用';
    return (
      <div className="mx-4 my-8 rounded-lg border border-dashed border-slate-200 py-16 text-center text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
        {emptyHint}
      </div>
    );
  }

  return (
    <div>
      {selectedIds.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs dark:border-slate-800 dark:bg-slate-900/60">
          <span className="font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">
            已选 {selectedIds.length} 封
          </span>
          {mode === 'trash' ? (
            <>
              <button
                type="button"
                onClick={() => runBulk(restore)}
                disabled={busy}
                className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 whitespace-nowrap"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                恢复
              </button>
              <button
                type="button"
                onClick={confirmPurge}
                disabled={busy}
                className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-red-200 bg-white px-2.5 font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-500/40 dark:bg-slate-900 dark:text-red-300 dark:hover:bg-red-500/10 whitespace-nowrap"
              >
                <Trash2 className="h-3.5 w-3.5" />
                彻底删除
              </button>
            </>
          ) : (
            <>
              <MoveToFolderSelect current={currentFolder} onMove={moveSelected} disabled={busy} />
              <button
                type="button"
                onClick={() => runBulk(trash)}
                disabled={busy}
                className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 whitespace-nowrap"
              >
                <Trash2 className="h-3.5 w-3.5" />
                移入回收站
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100 whitespace-nowrap"
          >
            取消选择
          </button>
        </div>
      ) : null}

      {/* 9 列表格在手机上打开即横滚、星标/删除被挤出屏外，所以 sm 以下整体换成卡片列表。
          两种形态复用同一份 items 与同一套 handler（见 useRowActions）。 */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-slate-100 text-left text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
            <tr>
              <th className="w-10 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="全选本页邮件"
                  className="h-3.5 w-3.5 cursor-pointer rounded border-slate-300 dark:border-slate-600"
                />
              </th>
              <th className="px-4 py-2.5 font-medium whitespace-nowrap">发件人</th>
              <th className="px-4 py-2.5 font-medium whitespace-nowrap">主题</th>
              <th className="px-4 py-2.5 font-medium whitespace-nowrap">收件地址</th>
              <th className="px-4 py-2.5 font-medium whitespace-nowrap">标签</th>
              <th className="px-4 py-2.5 font-medium whitespace-nowrap">时间</th>
              <th className="px-4 py-2.5 font-medium whitespace-nowrap">状态</th>
              <th className="w-10 px-2 py-2.5"></th>
              <th className="w-10 px-2 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {items.map((m) => (
              <Row
                key={m.id}
                m={m}
                mode={mode}
                selected={selected.has(m.id)}
                onToggleSelect={toggleOne}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ul className="divide-y divide-slate-100 sm:hidden dark:divide-slate-800">
        {items.map((m) => (
          <MessageCard
            key={m.id}
            m={m}
            mode={mode}
            selected={selected.has(m.id)}
            onToggleSelect={toggleOne}
          />
        ))}
      </ul>

      <div className="flex items-center justify-center border-t border-slate-100 px-4 py-3 dark:border-slate-800">
        {q.hasNextPage ? (
          <button
            type="button"
            onClick={() => void q.fetchNextPage()}
            disabled={q.isFetchingNextPage}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
          >
            {q.isFetchingNextPage ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                加载中...
              </>
            ) : (
              '加载更多'
            )}
          </button>
        ) : (
          <div className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
            已到底部 &middot; 共 {items.length} 封
          </div>
        )}
      </div>
    </div>
  );
}

interface RowProps {
  m: MessageDTO;
  mode: MessageListMode;
  selected: boolean;
  onToggleSelect: (id: number) => void;
}

/**
 * 行级状态 + 交互 handler。表格行（Row）与窄屏卡片（MessageCard）是同一份行数据的
 * 两种展示形态，业务逻辑只在这里写一遍，避免两边各自实现出现行为漂移。
 */
function useRowActions(m: MessageDTO, mode: MessageListMode) {
  const navigate = useNavigate();
  const trash = useTrashMessages();
  const restore = useRestoreMessages();
  const isUnread = m.readAt == null;
  const isForwarded = m.forwardedAt != null;

  const toggleStar = useToggleStar(m.id, m.starred);
  const markRead = useMarkRead(m.id);
  const markUnread = useMarkUnread(m.id);

  const domain = m.fromAddress.split('@')[1] ?? '';

  const handleRowClick = () => navigate(`/app/inbox/${m.id}`);

  const handleStar = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleStar.mutate();
  };

  const handleToggleRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isUnread) markRead.mutate();
    else markUnread.mutate();
  };

  const handleRowAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (mode === 'trash') restore.mutate([m.id]);
    else trash.mutate([m.id]);
  };

  return {
    isUnread,
    isForwarded,
    domain,
    handleRowClick,
    handleStar,
    handleToggleRead,
    handleRowAction,
    actionPending: trash.isPending || restore.isPending,
  };
}

function Row({ m, mode, selected, onToggleSelect }: RowProps) {
  const {
    isUnread,
    isForwarded,
    domain,
    handleRowClick,
    handleStar,
    handleToggleRead,
    handleRowAction,
    actionPending,
  } = useRowActions(m, mode);

  return (
    <tr
      onClick={handleRowClick}
      className={
        'cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-900/60 ' +
        (selected
          ? 'bg-brand-50 dark:bg-brand-500/10'
          : isUnread
            ? 'bg-brand-50/30 dark:bg-brand-500/5'
            : '')
      }
    >
      {/* 勾选框：stopPropagation 防止点它就跳详情 */}
      <td className="w-10 px-3 py-3 align-top" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(m.id)}
          aria-label={`选择邮件：${m.subject || '(无主题)'}`}
          className="mt-1 h-3.5 w-3.5 cursor-pointer rounded border-slate-300 dark:border-slate-600"
        />
      </td>

      {/* 发件人：favicon + name + address 竖排 */}
      <td className="px-4 py-3 align-top">
        <div className="flex items-start gap-2.5">
          <SenderAvatar
            fromAddress={m.fromAddress}
            fromName={m.fromName}
            className="mt-0.5 h-5 w-5"
          />
          <div className="min-w-0">
            <div
              className={
                // 不能用 truncate（overflow:hidden 会裁掉徽标浮层）；截断交给内层 span。
                'flex min-w-0 items-center gap-1 text-sm ' +
                (isUnread
                  ? 'font-semibold text-slate-900 dark:text-slate-50'
                  : 'font-medium text-slate-700 dark:text-slate-200')
              }
            >
              <span className="truncate">{m.fromName ?? domain ?? m.fromAddress}</span>
              <AuthSuspiciousBadge failed={m.authFailed} />
            </div>
            <div className="truncate text-xs text-slate-500 dark:text-slate-400">
              {m.fromAddress}
            </div>
          </div>
        </div>
      </td>

      {/* 主题 */}
      <td className="px-4 py-3 align-top">
        <div className="max-w-md">
          <Link
            to={`/app/inbox/${String(m.id)}`}
            onClick={(e) => e.stopPropagation()}
            className={
              'block truncate text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500 ' +
              (isUnread
                ? 'font-semibold text-slate-900 dark:text-slate-50'
                : 'text-slate-700 dark:text-slate-200')
            }
          >
            {m.subject || '(无主题)'}
          </Link>
          {m.snippet ? (
            <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
              {m.snippet}
            </div>
          ) : null}
        </div>
      </td>

      {/* 收件地址 */}
      <td className="px-4 py-3 align-top text-xs text-slate-600 dark:text-slate-300">
        <div className="max-w-xs truncate" title={m.toAddress}>
          {m.toAddress}
        </div>
      </td>

      {/* 标签 */}
      <td className="px-4 py-3 align-top">
        <div className="flex max-w-[10rem] flex-wrap gap-1">
          {m.labels.length === 0 ? (
            <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
          ) : (
            m.labels.map((l) => <LabelChip key={l.id} label={l} size="sm" />)
          )}
        </div>
      </td>

      {/* 时间 */}
      <td className="px-4 py-3 align-top text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
        {formatRelative(m.receivedAt)}
      </td>

      {/* 状态 chip：读/未读/已转发 */}
      <td className="px-4 py-3 align-top whitespace-nowrap">
        <div className="flex flex-wrap items-center gap-1.5">
          <ReadChip isUnread={isUnread} onClick={handleToggleRead} />
          {isForwarded ? <ForwardedChip /> : null}
        </div>
      </td>

      {/* 星标 */}
      <td className="w-10 px-2 py-3 align-top">
        <StarButton starred={m.starred} onClick={handleStar} className="h-7 w-7" />
      </td>

      {/* 删除 / 恢复 */}
      <td className="w-10 px-2 py-3 align-top">
        <RowActionButton
          mode={mode}
          disabled={actionPending}
          onClick={handleRowAction}
          className="h-7 w-7"
        />
      </td>
    </tr>
  );
}

/**
 * 窄屏卡片形态（sm 以下）：四行紧凑排版，勾选态与所有操作按钮都保留，
 * 触控目标放大到 h-9 w-9（表格里是 h-7，鼠标够用但手指不够）。
 */
function MessageCard({ m, mode, selected, onToggleSelect }: RowProps) {
  const {
    isUnread,
    isForwarded,
    domain,
    handleRowClick,
    handleStar,
    handleToggleRead,
    handleRowAction,
    actionPending,
  } = useRowActions(m, mode);

  return (
    <li
      onClick={handleRowClick}
      className={
        'cursor-pointer px-3 py-3 transition-colors ' +
        (selected
          ? 'bg-brand-50 dark:bg-brand-500/10'
          : isUnread
            ? 'bg-brand-50/30 dark:bg-brand-500/5'
            : '')
      }
    >
      <div className="flex items-start gap-2.5">
        {/* 勾选框：stopPropagation 防止点它就跳详情 */}
        <span className="pt-1" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(m.id)}
            aria-label={`选择邮件：${m.subject || '(无主题)'}`}
            className="touch-target h-4 w-4 cursor-pointer rounded border-slate-300 dark:border-slate-600"
          />
        </span>

        <div className="min-w-0 flex-1">
          {/* 第一行：发件人 + 时间 */}
          <div className="flex items-center gap-2">
            <SenderAvatar fromAddress={m.fromAddress} fromName={m.fromName} className="h-5 w-5" />
            <span
              className={
                'min-w-0 flex-1 truncate text-sm ' +
                (isUnread
                  ? 'font-semibold text-slate-900 dark:text-slate-50'
                  : 'font-medium text-slate-700 dark:text-slate-200')
              }
            >
              {m.fromName ?? domain ?? m.fromAddress}
            </span>
            <AuthSuspiciousBadge failed={m.authFailed} />
            <span className="shrink-0 text-[11px] text-slate-500 whitespace-nowrap dark:text-slate-400">
              {formatRelative(m.receivedAt)}
            </span>
          </div>

          {/* 第二行：主题（未读加粗） */}
          <Link
            to={`/app/inbox/${String(m.id)}`}
            onClick={(e) => e.stopPropagation()}
            className={
              'mt-1 block truncate text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500 ' +
              (isUnread
                ? 'font-semibold text-slate-900 dark:text-slate-50'
                : 'text-slate-700 dark:text-slate-200')
            }
          >
            {m.subject || '(无主题)'}
          </Link>

          {/* 第三行：收件地址 */}
          <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
            {m.toAddress}
          </div>

          {/* 第四行：标签 + 状态 + 星标 / 删除 */}
          <div className="mt-2 flex items-center gap-1.5">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
              <ReadChip isUnread={isUnread} onClick={handleToggleRead} />
              {isForwarded ? <ForwardedChip /> : null}
              {m.labels.map((l) => (
                <LabelChip key={l.id} label={l} size="sm" />
              ))}
            </div>
            <StarButton starred={m.starred} onClick={handleStar} className="h-9 w-9" />
            <RowActionButton
              mode={mode}
              disabled={actionPending}
              onClick={handleRowAction}
              className="h-9 w-9"
            />
          </div>
        </div>
      </div>
    </li>
  );
}

// ==================== 两种形态共用的小组件 ====================

function ReadChip({
  isUnread,
  onClick,
}: {
  isUnread: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={isUnread ? '标记为已读' : '标记为未读'}
      aria-label={isUnread ? '标记为已读' : '标记为未读'}
      className={
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap transition-colors ' +
        (isUnread
          ? 'bg-brand-100 text-brand-700 hover:bg-brand-200 dark:bg-brand-500/20 dark:text-brand-200'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700')
      }
    >
      {isUnread ? '未读' : '已读'}
    </button>
  );
}

function ForwardedChip() {
  return (
    <span
      className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700 whitespace-nowrap dark:bg-emerald-500/20 dark:text-emerald-300"
      title="已转发"
    >
      已转发
    </span>
  );
}

function StarButton({
  starred,
  onClick,
  className,
}: {
  starred: boolean;
  onClick: (e: React.MouseEvent) => void;
  className: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={starred ? '取消星标' : '添加星标'}
      aria-label={starred ? '取消星标' : '添加星标'}
      className={
        // touch-target：视觉尺寸不变，触屏下热区补到 44×44（两个按钮紧邻容易误点）
        `touch-target inline-flex shrink-0 items-center justify-center rounded-md transition-colors ${className} ` +
        (starred
          ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10'
          : 'text-slate-400 hover:bg-slate-100 hover:text-amber-500 dark:text-slate-500 dark:hover:bg-slate-800')
      }
    >
      {starred ? <Star className="h-4 w-4" fill="currentColor" /> : <StarOff className="h-4 w-4" />}
    </button>
  );
}

function RowActionButton({
  mode,
  disabled,
  onClick,
  className,
}: {
  mode: MessageListMode;
  disabled: boolean;
  onClick: (e: React.MouseEvent) => void;
  className: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={mode === 'trash' ? '恢复到收件箱' : '移入回收站'}
      aria-label={mode === 'trash' ? '恢复到收件箱' : '移入回收站'}
      className={`touch-target inline-flex shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-red-500 disabled:opacity-50 dark:text-slate-500 dark:hover:bg-slate-800 ${className}`}
    >
      {mode === 'trash' ? <RotateCcw className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
    </button>
  );
}
